/**
 * Web Push helpers — register the service worker, ask permission, and
 * send the browser's PushSubscription to the backend.
 *
 * Usage:
 *   import { enablePush, disablePush, isPushEnabled } from '../lib/pushNotifications';
 *   const state = await isPushEnabled();  // 'unsupported' | 'denied' | 'granted' | 'default'
 *   await enablePush();
 *   await disablePush();
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';

// Convert the URL-safe Base64 VAPID key to a Uint8Array (required by PushManager).
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

export function isPushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export async function getPushStatus() {
  if (!isPushSupported()) return 'unsupported';
  if (!VAPID_PUBLIC_KEY) return 'unavailable';
  return Notification.permission; // 'default' | 'granted' | 'denied'
}

async function registerServiceWorker() {
  if (!navigator.serviceWorker.controller) {
    try {
      const existing = await navigator.serviceWorker.getRegistration('/sw.js');
      if (existing) return existing;
    } catch {
      /* ignore */
    }
  }
  return navigator.serviceWorker.register('/sw.js');
}

export async function enablePush() {
  if (!isPushSupported()) throw new Error('Push notifications are not supported on this device.');
  if (!VAPID_PUBLIC_KEY) throw new Error('Push notifications are not configured on this server.');
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error(permission === 'denied'
      ? 'You blocked notifications. Enable them in your browser settings.'
      : 'Permission was not granted.');
  }
  const registration = await registerServiceWorker();
  await navigator.serviceWorker.ready;

  // Reuse existing subscription if present, else create a new one.
  let sub = await registration.pushManager.getSubscription();
  if (!sub) {
    sub = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
  }
  await axios.post(`${API}/notifications/push-subscribe`, { subscription: sub.toJSON() });
  return true;
}

export async function disablePush() {
  if (!isPushSupported()) return false;
  try {
    const registration = await navigator.serviceWorker.getRegistration('/sw.js');
    if (!registration) return false;
    const sub = await registration.pushManager.getSubscription();
    if (!sub) return false;
    try {
      await axios.post(`${API}/notifications/push-unsubscribe`, { subscription: sub.toJSON() });
    } catch {
      /* ignore backend errors — still unsubscribe locally */
    }
    await sub.unsubscribe();
    return true;
  } catch (e) {
    console.warn('disablePush failed:', e);
    return false;
  }
}

export async function sendTestPush() {
  await axios.post(`${API}/notifications/push-test`);
}
